import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { scheduleService, taskService, taskScheduleService } from '../services';
import {
  formatData,
  handleError,
  Role,
  cronDescription,
  getTimezoneAbbreviation,
} from '../helpers';
import {
  DataTable,
  MainColumn,
  MorphButton,
  ObjectDetail,
  ObjectDetails,
  Tabs,
} from '../components';
import {
  useInterval,
  useSideDialog,
  useModalDialog,
  useGlobalState,
  useNotFound,
  useMessage,
} from '../hooks';

/**
 * Detail view for a single schedule.
 * Shows schedule metadata in the header and all assigned tasks in a Tasks tab.
 * Polls every 3 s to reflect live task job status.
 */
export const Schedule = () => {
  const [schedule, setSchedule] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [tasksAll, setTasksAll] = useState([]);
  const [loggedInUser] = useGlobalState('loggedInUser');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);

  const clearSelectedItems = () => setSelectedItems([]);

  const { schedule_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { notFound, setNotFound, NotFound } = useNotFound();

  useInterval(() => {
    fetchTasks();
  }, 3000);

  useEffect(() => {
    fetchItems();
    fetchTasks();
    fetchAllTasks();
  }, [schedule_id]);

  /**
   * Fetches the schedule metadata and enriches it with human-readable cron description.
   */
  const fetchItems = async () => {
    scheduleService
      .getById(schedule_id)
      .then(res => {
        if (!res) return;

        if (!res.schedules || res.schedules.length < 1) {
          setNotFound(true);
        } else {
          const s = res.schedules[0];
          const desc = cronDescription(s.schedule_cron);
          const abbr = getTimezoneAbbreviation(s.schedule_timezone || 'UTC');
          setSchedule({
            ...s,
            schedule_cron_description: desc ? `${desc} (${abbr})` : desc,
            schedule_next_run_timestamp: s.schedule_is_enabled
              ? formatData(s.schedule_next_run_timestamp, 'datetime')
              : 'Disabled',
          });
          setLoading(false);
        }
      })
      .catch(err => {
        handleError({ err, showMessage, setNotFound });
      });
  };

  /**
   * Fetches all tasks assigned to this schedule with live job status.
   */
  const fetchTasks = async () => {
    taskService
      .getByScheduleId(schedule_id)
      .then(res => {
        if (!res) return;
        const tasks_tmp = res.tasks.map(task => ({
          ...task,
          task_is_active: task.job_status === 'running' || task.job_queue_position != null,
        }));
        setTasks(tasks_tmp);
        setSelectedItems(prev =>
          prev.map(sel => tasks_tmp.find(t => t.task_id === sel.task_id) ?? sel)
        );
        setLoadingTable(false);
      })
      .catch(err => {
        handleError({ err, showMessage }, () => {
          setLoadingTable(false);
        });
      });
  };

  /**
   * Fetches all tasks to populate the MorphButton assignment dropdown.
   */
  const fetchAllTasks = async () => {
    taskService
      .getAll()
      .then(res => {
        if (!res) return;
        setTasksAll(res.tasks);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Toggle handler for the MorphButton. Assigns the task to this schedule if not
   * already assigned, or removes it if it is.
   * @param {{ value: number }} event - Select change event with the selected task_id.
   */
  const updateTask = async event => {
    const { value } = event;
    const existingTask = tasks.find(x => x.task_id === value);

    if (existingTask) {
      await removeFromSchedule([existingTask]);
    } else {
      await assignToSchedule(value);
    }
  };

  /**
   * Assigns a task to this schedule via the task_schedule junction endpoint.
   * @param {number} taskId - The task to assign.
   */
  const assignToSchedule = async taskId => {
    await taskScheduleService
      .create({ task_schedules: [{ task_id: taskId, schedule_id: parseInt(schedule_id) }] })
      .then(res => {
        fetchTasks();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Removes one or more tasks from this schedule via the task_schedule junction endpoint.
   * Does NOT delete the task entity.
   * @param {Object[]} data - Task objects to remove from this schedule.
   */
  const removeFromSchedule = async data => {
    await taskScheduleService
      .destroy({
        task_schedules: data.map(t => ({ task_id: t.task_id, schedule_id: parseInt(schedule_id) })),
      })
      .then(res => {
        hideModalDialog();
        clearSelectedItems();
        fetchTasks();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Updates the optimistic job status for a set of tasks before the API confirms.
   * @param {Object[]} task_arr - Current tasks array.
   * @param {Function} setNewState - State setter.
   * @param {Object[]} jobs - Updated job objects with task_id and job_status.
   */
  const updateJobStatus = (task_arr, setNewState, jobs) => {
    let tasks_tmp = task_arr;
    jobs.forEach(job => {
      const i = task_arr.findIndex(x => x.task_id === job.task_id);
      if (i !== -1) {
        tasks_tmp[i] = {
          ...tasks_tmp[i],
          job_status: job.job_status,
          job_completion_message: job.job_completion_message,
        };
      }
    });
    setNewState([...tasks_tmp]);
  };

  /**
   * Manually enqueues all eligible tasks assigned to this schedule.
   * Disabled, running, and already-queued tasks are silently skipped by the backend.
   */
  const runSchedule = async () => {
    await scheduleService
      .run([data.schedule_id])
      .then(res => res && showMessage(res.message))
      .catch(err => showMessage(err, 'error'));
  };

  /**
   * Edits the schedule via the API.
   * @param {Object|Object[]} data - Schedule data from the edit form.
   */
  const editSchedule = async data => {
    const payload = { schedules: Array.isArray(data) ? data : [data] };

    await scheduleService
      .update(payload)
      .then(res => {
        fetchItems();
        showMessage(res.message);
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  /**
   * Deletes the schedule and navigates back to the Schedules list.
   * @param {Object[]} data - Selected schedule objects.
   */
  const deleteSchedule = async data => {
    await scheduleService
      .destroy({ schedules: data })
      .then(res => {
        hideModalDialog();
        showMessage(res.message);
        if (location.key !== 'default') {
          // Arrived here by navigating inside Katalogue, so return the user where they came from.
          navigate(-1);
        } else {
          // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
          // history to return to. Fall back to the deleted asset's parent.
          navigate('/schedules');
        }
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  /**
   * Enqueues one or more tasks for execution via a single batch request.
   * Optimistically marks selected tasks as 'queued' in the UI before the request completes.
   * @param {Object[]} [data] - Explicit task list; falls back to selectedItems.
   */
  const runTask = async data => {
    const selectedTasks = data || selectedItems;

    updateJobStatus(
      tasks,
      setTasks,
      selectedTasks.map(task => ({
        task_id: task.task_id,
        job_status: 'queued',
        job_completion_message: 'Queued at ' + new Date().toLocaleString('sv-SE'),
      }))
    );
    setSelectedItems(prev =>
      prev.map(sel =>
        selectedTasks.find(t => t.task_id === sel.task_id) ? { ...sel, task_is_active: true } : sel
      )
    );

    await taskService
      .runMany(selectedTasks.map(t => t.task_id))
      .then(res => {
        res && showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      })
      .finally(() => {
        fetchTasks();
      });
  };

  /**
   * Aborts one or more tasks via a single batch request.
   * Queued tasks are dequeued immediately; running tasks receive an abort signal asynchronously.
   * @param {Object[]} [data] - Explicit task list; falls back to selectedItems.
   */
  const abortTask = async data => {
    const selectedTasks = data || selectedItems;

    await taskService
      .abortMany(selectedTasks.map(t => t.task_id))
      .then(res => res && showMessage(res.message))
      .catch(err => showMessage(err, 'error'))
      .finally(() => {
        fetchTasks();
      });
  };

  /**
   * Edits the selected tasks.
   * @param {Object|Object[]} data - Task data from the edit form.
   */
  const editTask = async data => {
    const payload = { tasks: Array.isArray(data) ? data : [data] };

    await taskService
      .update(payload)
      .then(res => {
        fetchTasks();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  const data = loading ? [] : schedule;
  const editable = loggedInUser && loggedInUser.user_role_name === Role.admin;

  const requestResults = tasksAll.map(t => ({
    value: t.task_id,
    label: t.task_name,
    tooltip: t.task_description,
  }));

  const taskIsEnabledValues = {
    true: { label: 'Enabled', className: 'pill' },
    false: { label: 'Disabled', className: 'pill pill-semifull-red' },
  };

  if (notFound) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="Schedule"
          title={data.schedule_name}
          subtitleText={data && !loading && data.schedule_cron_description}
          description={data.schedule_description}
          loading={loading}
          details={[
            { title: 'Last Modified', data: formatData(data.modified_timestamp, 'datetime') },
            { title: 'Created', data: formatData(data.created_timestamp, 'datetime') },
            {
              title: 'Id',
              data: data.schedule_id,
              tooltip: 'Katalogue-internal id for this asset',
            },
          ]}
          editable={editable}
          buttons={[
            {
              value: 'Edit',
              buttonStyle: 'main-button',
              onClick: () => showSideDialog('editSchedule', [data], editSchedule),
            },
            {
              value: 'Run',
              disabled: !data.schedule_is_enabled,
              tooltip: !data.schedule_is_enabled
                ? 'Schedule is disabled'
                : 'Run all tasks assigned to this schedule',
              onClick: runSchedule,
            },
            {
              value: 'Delete',
              onClick: () => showModalDialog('deleteSchedule', [data], deleteSchedule),
            },
          ]}
        >
          <ObjectDetail label="Cron" loading={loading} showLoadingForChildren={true}>
            <span className="code">{data.schedule_cron}</span>
          </ObjectDetail>

          <ObjectDetail label="Next Run" loading={loading} showLoadingForChildren={true}>
            {data.schedule_next_run_timestamp}
          </ObjectDetail>

          <ObjectDetail label="Last Run" loading={loading} showLoadingForChildren={true}>
            {formatData(data.schedule_last_enqueued_timestamp, 'datetime')}
          </ObjectDetail>

          <ObjectDetail label="Status" loading={loading} showLoadingForChildren={true}>
            {data.schedule_is_enabled ? (
              <span className="pill pill-enabled">Enabled</span>
            ) : (
              <span className="pill pill-semifull-red">Disabled</span>
            )}
          </ObjectDetail>
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs className="slim left">
          <div
            label="Tasks"
            tabid="tasks"
            resultCount={loadingTable || tasks.length === 0 ? undefined : tasks.length}
          >
            <DataTable
              columns={[
                { id: 'job_status', name: '', type: 'icon', tooltip: 'job_completion_message' },
                {
                  id: 'task_name',
                  name: 'Name',
                  link: '/tasks/:task_id',
                  tooltip: 'task_description',
                  className: 'bold',
                },
                {
                  id: 'task_type_name',
                  name: 'Type',
                  className: 'small-text',
                  tooltip: 'task_type_description',
                },
                { id: 'job_completed_timestamp', name: 'Last Run Completed', type: 'datetime' },
                {
                  id: 'job_runtime_seconds',
                  name: 'Runtime [s]',
                  type: 'chart',
                  chartType: 'bar',
                  data: { data: 'job_runtime_seconds', max: 'job_runtime_seconds_max_value' },
                  tooltip: 'job_runtime_tooltip',
                },

                { id: 'task_priority', name: 'Priority', type: 'integer', align: 'right' },

                {
                  id: 'task_is_enabled',
                  name: 'Status',
                  type: 'boolean',
                  values: taskIsEnabledValues,
                },
              ]}
              buttons={[
                {
                  customButton: (
                    <MorphButton
                      name="task_id"
                      value={tasks.map(t => ({ value: t.task_id, label: t.task_name }))}
                      options={requestResults}
                      onChange={updateTask}
                      placeholder="Search Tasks to add..."
                      onToggle={clearSelectedItems}
                    />
                  ),
                  action: 'add',
                  onClick: () => {},
                },
                {
                  label: 'Edit',
                  action: 'edit',
                  onClick: items => showSideDialog('editTask', items || selectedItems, editTask),
                },
                {
                  label: 'Run',
                  action: 'run',
                  onClick: runTask,
                  tooltip: 'Run selected tasks',
                  disabled: [
                    {
                      field: 'job_status',
                      value: 'running',
                      tooltip: 'One or more selected tasks are already running',
                    },
                    {
                      field: 'task_is_enabled',
                      value: false,
                      tooltip: 'All selected tasks are disabled',
                    },
                  ],
                },
                {
                  label: 'Cancel',
                  action: 'abort',
                  onClick: abortTask,
                  hidden: { field: 'task_is_active', value: false },
                },
                {
                  label: 'Remove',
                  action: 'remove',
                  tooltip: 'Remove task from this schedule. The task itself will not be deleted.',
                  onClick: items =>
                    showModalDialog(
                      'removeTaskFromSchedule',
                      items || selectedItems,
                      removeFromSchedule
                    ),
                },
              ]}
              data={tasks}
              idColumn="task_id"
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              loading={loadingTable}
              editable={editable}
              filterable={true}
              filterObjects={['task_name', 'task_description', 'task_type_name', 'task_is_enabled']}
            />
          </div>
        </Tabs>
      </div>
    </div>
  );
};
